import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSaleMode } from '../context';
import { deskingActions, deskingSelectors } from '../model';
import { ConvertParams, FinanceProgramFormValues } from '../types';
import { CALCULATE_CASH_PAYMENTS, CALCULATE_FINANCE_PAYMENTS } from '../api';
import { collectCashVariablesForCalc, collectFinanceVariablesForCalc } from '../lib';
import { CalculatePaymentCashVariables, CalculatePaymentFinanceVariables, CalculatingResponse } from '../api/types';

export function useCalculations() {
  const dispatch = useDispatch();

  const { isCash, isFinancing } = useSaleMode();

  const { control } = useFormContext<FinanceProgramFormValues>();
  const [financeQuote, saleDetails, financeProgramDescription, submission, isExcludeEquipmentSales] = useWatch({
    control,
    name: [
      'financeQuote',
      'saleDetails',
      'financeProgramDescription',
      'submission',
      'displaySettings.excludeEquipmentSales',
    ],
  });

  const recalculateStep = useSelector(deskingSelectors.recalculateStep);
  const proposalMenu = useSelector(deskingSelectors.proposalMenuByCurrentTerm);
  const proposalProductConfigurationsInMenu = useSelector(deskingSelectors.proposalProductConfigurationsInMenu);

  const calculationsVariables: ConvertParams = {
    program: financeQuote,
    submission,
    saleDetails,
    proposalMenu,
    isExcludeEquipmentSales,
    proposalProductConfigurationsInMenu,
    financeProgramDescription: financeProgramDescription.apiValue,
  };

  const {
    called,
    loading: isCalculating,
    refetch: recalculateMultiplePayments,
  } = useQuery<CalculatingResponse, CalculatePaymentFinanceVariables>(CALCULATE_FINANCE_PAYMENTS, {
    notifyOnNetworkStatusChange: true,
    skip: isCash || !financeQuote.createProposalWithExistingProgram,
    variables: collectFinanceVariablesForCalc(calculationsVariables),
    onCompleted(response) {
      if (!Array.isArray(response?.calculateMultiplePayments)) return;

      const isMenuExist = !!response?.calculateMultiplePayments
        .map(({ paymentOptions }) => !!paymentOptions?.[0]?.menu)
        ?.filter(Boolean)?.length;

      const results = response.calculateMultiplePayments[0]?.paymentOptions;

      if (isMenuExist) dispatch(deskingActions.setMenuPricing({ results }));
      else dispatch(deskingActions.setFinancePricing({ results }));
    },
  });

  useQuery<CalculatingResponse, CalculatePaymentCashVariables>(CALCULATE_CASH_PAYMENTS, {
    skip: isFinancing || !proposalMenu,
    notifyOnNetworkStatusChange: true,
    variables: collectCashVariablesForCalc(calculationsVariables),
    onCompleted(response) {
      if (!Array.isArray(response?.calculateMultiplePayments)) return;
      const results = response.calculateMultiplePayments[0]?.paymentOptions;
      dispatch(deskingActions.setMenuPricing({ results }));
    },
  });

  useEffect(() => {
    if (isFinancing && called) {
      recalculateMultiplePayments();
    }
  }, [recalculateStep, isFinancing, called]);

  return {
    isCalculating,
  };
}
